import React, { useState, useEffect, useCallback } from 'react';
import { formatDateTimeLocale } from '../../utils/dateFormat';
import { formatMoney, formatMoneyPrecise } from '../../utils/money';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { api } from '../../utils/api';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import { ArrowLeft, Loader2, Building2, Receipt, Calendar, CheckCircle, MessageCircle, CreditCard, AlertTriangle } from 'lucide-react';
import { toast } from '../../hooks/use-toast';
import { Toaster } from '../../components/Toaster';
import { OfferScopeDetails } from '../../components/patient/OfferScopeDetails';
import { PaymentPlanSchedule } from '../../components/payments/PaymentPlanSchedule';
import { fetchOrderPaymentState, paymentLinkFor } from '../../utils/paymentEligibility';

const PatientOrderDetail = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  // ORDER-CONTENT-01 — payment state is READ from the canonical eligibility
  // gate. Nothing is initiated while the page loads; the patient still has to
  // press Continue, which hands over to the existing PaymentPage flow (where
  // session reuse and idempotency live).
  const [payment, setPayment] = useState({ state: 'loading', data: null });

  const fetchOrderDetail = useCallback(async () => {
    try {
      setLoading(true);
      const response = await api.get(`/patient/orders/detail/${orderId}`);
      setOrder(response.data);
    } catch (error) {
      console.error('Error fetching order:', error);
      toast({
        title: t('patient.common.error'),
        description: t('patient.common.error'),
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  }, [orderId, t]);

  const refreshPaymentState = useCallback(async () => {
    setPayment({ state: 'loading', data: null });
    setPayment(await fetchOrderPaymentState(orderId));
  }, [orderId]);

  useEffect(() => { fetchOrderDetail(); }, [fetchOrderDetail]);
  useEffect(() => { refreshPaymentState(); }, [refreshPaymentState]);

  const getStatusColor = (status) => ({
    PENDING_CONFIRMATION: 'bg-yellow-100 text-yellow-800',
    CONFIRMED: 'bg-blue-100 text-blue-800',
    IN_PROGRESS: 'bg-purple-100 text-purple-800',
    COMPLETED: 'bg-green-100 text-green-800',
    CANCELLED: 'bg-red-100 text-red-800',
  }[status] || 'bg-gray-100 text-gray-800');

  const statusLabel = (status) =>
    t(`patient.orderStatus.${status}`, { defaultValue: String(status || '').replace(/_/g, ' ') });

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (!order) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Alert variant="destructive">
          <AlertDescription>{t('patient.common.empty')}</AlertDescription>
        </Alert>
      </div>
    );
  }

  const offer = order.quote_info;
  const fs = offer?.financial_summary || {};
  const elig = payment.data || {};
  const currency = elig.currency || offer?.currency || order.currency || 'EUR';
  const quoteId = offer?.quote_id || order.quote_id;

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 pb-12">
      <Toaster />

      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div className="flex items-center gap-3">
              <Button variant="ghost" size="sm" onClick={() => navigate('/patient/dashboard')} data-testid="order-back-btn">
                <ArrowLeft className="h-4 w-4 mr-2" />
                {t('adminNav.dashboard')}
              </Button>
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900">{t('patient.orderDetail.title')}</h1>
                <p className="text-xs text-gray-500">{t('patient.orderDetail.orderRef')}: ...{order.id.slice(-8)}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Badge className={getStatusColor(order.status)} data-testid="order-status-badge">
                {statusLabel(order.status)}
              </Badge>
              <Button
                variant="outline"
                size="sm"
                onClick={() => navigate('/patient/messages', { state: { clinicId: order?.clinic_id, subject: `Order: ${order?.treatment_name || 'Treatment'}`, contextId: order?.case_id || orderId, contextType: 'order' } })}
                className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                data-testid="order-message-clinic-btn"
              >
                <MessageCircle className="h-4 w-4 mr-2" />
                {t('patient.dashboard.openMessages')}
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6 space-y-5">
        {/* Resume — the single next action, above everything else. */}
        {payment.state === 'payable' && (
          <Card className="border-emerald-300 bg-emerald-50" data-testid="order-resume-payment-card">
            <CardContent className="py-4 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
              <div>
                <p className="font-semibold text-emerald-900" data-testid="order-amount-due-now">
                  {t('patient.orderDetail.dueNow')}: {formatMoneyPrecise(elig.amount, currency)}
                </p>
                <p className="text-xs text-emerald-800">
                  {t(`payment.obligationType.${elig.obligation_type}`, {
                    defaultValue: String(elig.obligation_type || '').replace(/_/g, ' '),
                  })}
                  {elig.installment_number ? ` · ${elig.installment_number}/${elig.installment_count}` : ''}
                </p>
              </div>
              <Button
                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                onClick={() => navigate(paymentLinkFor(order.id, quoteId))}
                data-testid="order-continue-payment-btn"
              >
                <CreditCard className="h-4 w-4 mr-2" />
                {t('patient.dashboard.continueToPayment')}
              </Button>
            </CardContent>
          </Card>
        )}
        {payment.state === 'settled' && (
          <Alert className="border-green-300 bg-green-50" data-testid="order-fully-paid-alert">
            <AlertDescription className="text-green-900">{t('patient.orderDetail.fullyPaid')}</AlertDescription>
          </Alert>
        )}
        {payment.state === 'scheduled' && (
          <Alert className="border-sky-300 bg-sky-50" data-testid="order-scheduled-alert">
            <AlertDescription className="text-sky-900">
              {t('patient.orderDetail.scheduledLater')}
              {elig.next_obligation?.amount ? (
                <span className="ml-1 font-medium" data-testid="order-next-scheduled-amount">
                  {formatMoneyPrecise(elig.next_obligation.amount,
                    elig.next_obligation.currency || currency)}
                  {elig.next_obligation.installment_number
                    ? ` · ${elig.next_obligation.installment_number}/${elig.next_obligation.installment_count}`
                    : ''}
                </span>
              ) : null}
            </AlertDescription>
          </Alert>
        )}
        {/* PLAN-ACTIVATION-STALL-01 — a settled predecessor whose successor
            was never released is a stalled activation, not future scheduling. */}
        {payment.state === 'activation_pending' && (
          <Alert className="border-sky-300 bg-sky-50" data-testid="order-activation-pending-alert">
            <AlertDescription className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-sky-900">
              <span>
                <span className="font-medium">{t('patient.orderDetail.activationPendingTitle')}</span>{' '}
                {t('patient.orderDetail.activationPendingBody')}
              </span>
              <Button variant="outline" size="sm" onClick={refreshPaymentState} data-testid="order-activation-pending-retry-btn">
                {t('patient.common.retry', { defaultValue: 'Try again' })}
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {/* PAYMENT-PLAN-STATE-01 — a missing/inconsistent schedule is NOT
            "scheduled later": it blocks payment and says why. */}
        {payment.state === 'incomplete' && (
          <Alert className="border-amber-300 bg-amber-50" data-testid="order-plan-incomplete-alert">
            <AlertDescription className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-amber-900">
              <span>
                <span className="font-medium">{t('patient.orderDetail.planIncompleteTitle')}</span>{' '}
                {t('patient.orderDetail.planIncompleteBody')}
              </span>
              <Button variant="outline" size="sm" onClick={refreshPaymentState} data-testid="order-plan-incomplete-retry-btn">
                {t('patient.common.retry', { defaultValue: 'Try again' })}
              </Button>
            </AlertDescription>
          </Alert>
        )}
        {payment.state === 'blocked_identity' && (
          <Alert className="border-amber-300 bg-amber-50" data-testid="order-identity-review-alert">
            <AlertDescription className="text-amber-900">
              {t('patient.orderDetail.identityReviewBody')}
            </AlertDescription>
          </Alert>
        )}
        {payment.state === 'blocked' && (
          <Alert className="border-amber-300 bg-amber-50" data-testid="order-payment-blocked-alert">
            <AlertDescription className="text-amber-900">{t('patient.orderDetail.paymentNotEnabled')}</AlertDescription>
          </Alert>
        )}
        {payment.state === 'unknown' && (
          <Alert className="border-slate-300 bg-slate-50" data-testid="order-payment-unknown-alert">
            <AlertDescription className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 text-slate-800">
              <span>{t('patient.orderDetail.paymentStateUnavailable')}</span>
              <Button variant="outline" size="sm" onClick={refreshPaymentState} data-testid="order-payment-retry-btn">
                {t('patient.common.retry', { defaultValue: 'Try again' })}
              </Button>
            </AlertDescription>
          </Alert>
        )}

        {/* Clinic — display identity only. */}
        {order.clinic_info && (
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base" data-testid="order-clinic-name">
                <Building2 className="h-5 w-5" />
                {order.clinic_info.clinic_name}
              </CardTitle>
              {(order.clinic_info.city || order.clinic_info.country) && (
                <CardDescription>
                  {[order.clinic_info.city, order.clinic_info.country].filter(Boolean).join(', ')}
                </CardDescription>
              )}
            </CardHeader>
          </Card>
        )}

        {/* Money — each figure labelled for what it is. */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Receipt className="h-5 w-5" />
              {t('patient.orderDetail.financialSummary')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
              <div className="rounded-lg border bg-white p-3" data-testid="order-contract-total">
                <p className="text-xs text-gray-500">{t('patient.orderDetail.contractTotal')}</p>
                <p className="text-lg font-bold text-gray-900">
                  {Number.isFinite(elig.contract_total) ? formatMoney(elig.contract_total, currency)
                    : Number.isFinite(fs.total) ? formatMoney(fs.total, currency) : '—'}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-3" data-testid="order-paid-total">
                <p className="text-xs text-gray-500">{t('patient.orderDetail.paidSoFar')}</p>
                <p className="text-lg font-bold text-green-700">
                  {Number.isFinite(elig.settled_total) ? formatMoney(elig.settled_total, currency) : '—'}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-3" data-testid="order-remaining-total">
                <p className="text-xs text-gray-500">{t('patient.orderDetail.remainingBalance')}</p>
                <p className="text-lg font-bold text-gray-900">
                  {Number.isFinite(elig.remaining_total) ? formatMoney(elig.remaining_total, currency) : '—'}
                </p>
              </div>
              <div className="rounded-lg border bg-white p-3" data-testid="order-due-now-total">
                <p className="text-xs text-gray-500">{t('patient.orderDetail.dueNow')}</p>
                <p className="text-lg font-bold text-emerald-700">
                  {payment.state === 'payable' && Number.isFinite(elig.amount)
                    ? formatMoney(elig.amount, currency)
                    : payment.state === 'settled' ? formatMoney(0, currency) : '—'}
                </p>
              </div>
            </div>

            {/* The accepted plan, exactly as derived — never a client split. */}
            {Array.isArray(elig.obligations) && elig.obligations.length > 0 ? (
              <div data-testid="order-obligation-schedule">
                <h3 className="font-semibold text-sm text-gray-700 mb-2">{t('patient.orderDetail.paymentSchedule')}</h3>
                <ol className="text-sm text-gray-700 space-y-1">
                  {elig.obligations.map((o) => (
                    <li key={o.sequence} className="flex flex-wrap justify-between gap-2 border-b last:border-0 py-1"
                        data-testid={`order-obligation-row-${o.sequence}`}>
                      <span>
                        {t(`payment.obligationType.${o.type}`, { defaultValue: String(o.type || '').replace(/_/g, ' ') })}
                        {o.installment_number ? ` ${o.installment_number}/${o.installment_count}` : ''}
                      </span>
                      <span className="flex items-center gap-2">
                        <span className="font-medium">{formatMoneyPrecise(o.amount, o.currency || currency)}</span>
                        <Badge className={o.status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-600'}>
                          {t(`payment.obligationStatus.${o.status}`, { defaultValue: String(o.status || '').replace(/_/g, ' ') })}
                        </Badge>
                      </span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : (
              offer && (
                <div data-testid="order-accepted-terms">
                  <h3 className="font-semibold text-sm text-gray-700 mb-2">{t('patient.orderDetail.acceptedTerms')}</h3>
                  <PaymentPlanSchedule
                    summary={fs}
                    currency={currency}
                    depositType={offer.deposit_type}
                    depositValue={offer.deposit_value}
                    testId="order-payment-plan"
                  />
                </div>
              )
            )}
          </CardContent>
        </Card>

        {/* The accepted offer — resolved strictly by this order's quote_id. */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <CheckCircle className="h-5 w-5" />
              {t('patient.orderDetail.acceptedScope')}
            </CardTitle>
            {offer && (
              <CardDescription data-testid="order-offer-reference">
                {offer.treatment_name || order.treatment_name || ''}
                {offer.version_number ? ` · v${offer.version_number}` : ''}
                {quoteId ? ` · ...${String(quoteId).slice(-8)}` : ''}
              </CardDescription>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            {order.offer_content_status !== 'available' ? (
              <Alert variant="destructive" data-testid="order-offer-content-missing">
                <AlertTriangle className="h-4 w-4" />
                <AlertDescription>
                  {order.offer_content_status === 'inconsistent'
                    ? t('patient.orderDetail.offerInconsistent')
                    : t('patient.orderDetail.offerMissing')}
                </AlertDescription>
              </Alert>
            ) : (
              <>
                <OfferScopeDetails offer={offer} idBase={quoteId} />
                {offer.notes_patient_visible && (
                  <div className="p-3 bg-blue-50 rounded-lg">
                    <p className="text-sm font-medium text-gray-700 mb-1">{t('patient.orderDetail.clinicNotes')}</p>
                    <p className="text-sm text-gray-600 whitespace-pre-line">{offer.notes_patient_visible}</p>
                  </div>
                )}
                {offer.terms && String(offer.terms).trim() && (
                  <div data-testid="order-offer-terms">
                    <h3 className="font-semibold text-sm text-gray-700 mb-1">
                      {t('patient.offers.termsAndConditions', 'Terms & Conditions')}
                    </h3>
                    <p className="text-sm text-gray-600 whitespace-pre-line">{offer.terms}</p>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>

        {/* Timeline */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Calendar className="h-5 w-5" />
              {t('patient.timeline.title')}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center gap-3">
              <div className="w-2 h-2 bg-blue-600 rounded-full" />
              <div>
                <p className="font-medium">{t('patient.orderDetail.createdOn')}</p>
                <p className="text-sm text-gray-500">{formatDateTimeLocale(order.created_at)}</p>
              </div>
            </div>
            {order.confirmed_at && (
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-green-600 rounded-full" />
                <div>
                  <p className="font-medium">{t('patient.booking.milestone_confirmed')}</p>
                  <p className="text-sm text-gray-500">{formatDateTimeLocale(order.confirmed_at)}</p>
                </div>
              </div>
            )}
            {order.completed_at && (
              <div className="flex items-center gap-3">
                <div className="w-2 h-2 bg-purple-600 rounded-full" />
                <div>
                  <p className="font-medium">{t('patient.booking.milestone_completed')}</p>
                  <p className="text-sm text-gray-500">{formatDateTimeLocale(order.completed_at)}</p>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        <Alert>
          <AlertDescription>{t('patient.orderDetail.supportNotice')}</AlertDescription>
        </Alert>
      </div>
    </div>
  );
};

export default PatientOrderDetail;
